import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import SEO from "@/components/SEO";
import { STORE_NAME } from "@/lib/seo";
import { useSettings } from "@/context/SettingsContext";

const EFFECTIVE_DATE = "07 October 2026";
const PHONE = "+91 93720 14662";

// ---------------------------------------------------------------------------
// Shared presentation primitives (keep legal pages consistent + readable).
// ---------------------------------------------------------------------------
function Section({ title, children, testid }) {
    return (
        <section className="space-y-3" data-testid={testid}>
            <h2 className="font-display text-xl md:text-2xl font-black uppercase tracking-tight text-white">
                {title}
            </h2>
            <div className="space-y-3 text-white/75 leading-relaxed">{children}</div>
        </section>
    );
}

function List({ items, testid }) {
    return (
        <ul className="list-disc pl-6 space-y-2" data-testid={testid}>
            {items.map((it, i) => (
                <li key={i}>{it}</li>
            ))}
        </ul>
    );
}

// Inline cross-links between policies. We detect the exact phrases the
// approved copy uses and replace them with React-Router Link components so
// SPA navigation stays client-side.
function PolicyText({ text, supportEmail }) {
    // Build the replacement map: longer phrases first so we don't split
    // "Return & Cancellation Policy" into "Cancellation Policy" etc.
    const replacements = [
        { phrase: "Return & Cancellation Policy", to: "/policy/returns" },
        { phrase: "Payment Terms", to: "/policy/payments" },
        { phrase: "Shipping Policy", to: "/policy/shipping" },
        { phrase: "Privacy Policy", to: "/policy/privacy" },
        { phrase: "Terms & Conditions", to: "/policy/terms" },
    ];

    // Build a single regex matching any of the phrases.
    const pattern = new RegExp(
        `(${replacements.map((r) => r.phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
        "g"
    );

    const pieces = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    // reset lastIndex on the regex since it's reused
    pattern.lastIndex = 0;
    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
            pieces.push(<span key={`t-${key++}`}>{text.slice(lastIndex, match.index)}</span>);
        }
        const matched = match[0];
        const route = replacements.find((r) => r.phrase === matched)?.to;
        pieces.push(
            <Link
                key={`l-${key++}`}
                to={route}
                className="text-[#00BCE5] hover:underline"
            >
                {matched}
            </Link>
        );
        lastIndex = match.index + matched.length;
    }
    if (lastIndex < text.length) {
        pieces.push(<span key={`t-${key++}`}>{text.slice(lastIndex)}</span>);
    }
    return <>{pieces}</>;
}

function P({ text, supportEmail }) {
    return (
        <p>
            <PolicyText text={text} supportEmail={supportEmail} />
        </p>
    );
}

function ContactLine({ supportEmail, phone, label }) {
    return (
        <p>
            {label}{" "}
            <a
                href={`mailto:${supportEmail}`}
                className="text-[#00BCE5] hover:underline"
            >
                {supportEmail}
            </a>{" "}
            or{" "}
            <a
                href={`tel:${phone.replace(/\s/g, "")}`}
                className="text-[#00BCE5] hover:underline"
            >
                {phone}
            </a>
            .
        </p>
    );
}

// ---------------------------------------------------------------------------
// 1. TERMS & CONDITIONS  — verbatim from approved document section "1."
// ---------------------------------------------------------------------------
function TermsBody({ supportEmail }) {
    return (
        <div className="space-y-8" data-testid="policy-terms-body">
            <P
                supportEmail={supportEmail}
                text="These Terms & Conditions govern the use of the Opensea Fishing Tackles website and all orders placed through it. By browsing the website, creating an account, or placing an order, you agree to these Terms & Conditions together with our Payment Terms, Shipping Policy, Return & Cancellation Policy and Privacy Policy."
            />

            <Section title="1. Business and Products" testid="terms-business">
                <P
                    supportEmail={supportEmail}
                    text="Opensea Fishing Tackles sells physical fishing tackle and related merchandise, including rods, reels, lures, lines, hooks, terminal tackle, accessories and other fishing products. Products may be sold as regular disclosed-product orders, sale or clearance orders, bulk/multiple-quantity orders, or mystery/undisclosed-product orders as described below."
                />
            </Section>

            <Section title="2. Regular Product Orders" testid="terms-regular">
                <P
                    supportEmail={supportEmail}
                    text="All products are subject to availability. Prices are displayed in Indian Rupees (INR / ₹) and include applicable taxes unless specifically stated otherwise. Product photographs are representative. Minor differences in colour, packaging, batch design or manufacturer labelling may occur. We may correct genuine listing, pricing, stock or description errors before dispatch and will contact the customer where the correction materially affects the order."
                />
            </Section>

            <Section title="3. Mystery / Undisclosed Product Orders" testid="terms-mystery">
                <P
                    supportEmail={supportEmail}
                    text="From time to time, Opensea Fishing Tackles may offer a product purchase in which the exact fishing product is not disclosed before payment. Every successfully paid mystery/undisclosed-product order is a purchase of physical fishing merchandise and is guaranteed to receive a physical product. The exact product is allocated after successful payment from the eligible inventory applicable to that offer. After allocation, the actual product supplied is recorded against the order and an invoice is generated for that physical product. Where a promotional discount is applicable, the invoice may show the product price and the applicable discount so that the final invoice value corresponds to the amount payable for the order. There is no successfully paid mystery/undisclosed-product order in which the customer receives no physical product. The customer accepts, before placing such an order, that the exact make, model, colour, size, category or product may not be known until allocation, unless the offer page states otherwise. A mystery/undisclosed-product order cannot be returned merely because the customer would have preferred a different eligible product. Damage, wrong-item, or other valid claims remain subject to the Return & Cancellation Policy and applicable law."
                />
            </Section>

            <Section title="4. Orders and Customer Information" testid="terms-orders">
                <P
                    supportEmail={supportEmail}
                    text="An order is accepted only after successful payment, or after verification where another payment method is expressly offered. Customers must provide a correct name, mobile number, email address and delivery address. Customers may place more than one order or purchase multiple quantities, subject to stock availability, per-customer limits and any offer-specific restrictions displayed on the website. We may cancel or refuse an order where there is stock unavailability, a genuine pricing error, suspected fraud, payment irregularity, abuse of an offer, or a non-serviceable delivery location."
                />
            </Section>

            <Section title="5. Payments and Invoices" testid="terms-payments">
                <P
                    supportEmail={supportEmail}
                    text="Payments are governed by the separately published Payment Terms. A tax invoice/order invoice is issued for the physical product or products supplied, in accordance with the information available at the time of invoicing and applicable tax requirements."
                />
            </Section>

            <Section title="6. Shipping, Cancellation, Returns and Damage" testid="terms-shipping-returns">
                <P
                    supportEmail={supportEmail}
                    text="Shipping timelines, charges and serviceability are governed by our Shipping Policy. Cancellations, refunds, transit-damage claims, wrong-item claims and any permitted replacements are governed by our Return & Cancellation Policy."
                />
            </Section>

            <Section title="7. Website Use" testid="terms-website-use">
                <P
                    supportEmail={supportEmail}
                    text="You must not use the website for any unlawful, fraudulent or abusive purpose. You must not attempt to interfere with, overload, scrape, reverse-engineer or compromise the normal operation or security of the website. Website text, images, branding, design and other original content belong to Opensea Fishing Tackles or their respective rights holders and may not be reused without permission."
                />
            </Section>

            <Section title="8. Communications" testid="terms-communications">
                <P
                    supportEmail={supportEmail}
                    text="By placing an order, you consent to receive transactional communications relating to payment, order confirmation, product allocation where applicable, invoicing, shipping, delivery and customer support through the contact details provided by you. Marketing communications, where sent, may be opted out of separately."
                />
            </Section>

            <Section title="9. Limitation and Applicable Law" testid="terms-limitation">
                <P
                    supportEmail={supportEmail}
                    text="To the extent permitted by applicable law, Opensea Fishing Tackles will not be responsible for indirect or consequential loss arising from misuse of products, manufacturer-related limitations, courier delays, events beyond reasonable control, connectivity failures or payment-gateway downtime. Nothing in these Terms limits rights or remedies that cannot lawfully be excluded under applicable Indian law."
                />
            </Section>

            <Section title="10. Changes and Contact" testid="terms-contact">
                <P
                    supportEmail={supportEmail}
                    text="We may update these Terms from time to time. The version published on the website at the time an order is placed will apply to that order unless a change is required by law."
                />
                <ContactLine
                    supportEmail={supportEmail}
                    phone={PHONE}
                    label="For questions, contact"
                />
            </Section>
        </div>
    );
}

// ---------------------------------------------------------------------------
// 2. PAYMENT TERMS  — verbatim from approved document section "2."
// ---------------------------------------------------------------------------
function PaymentsBody({ supportEmail }) {
    return (
        <div className="space-y-8" data-testid="policy-payments-body">
            <P
                supportEmail={supportEmail}
                text="These Payment Terms apply to all purchases made through the Opensea Fishing Tackles website, including regular product orders, sale/clearance orders, bulk or multiple-quantity purchases, and mystery/undisclosed-product orders."
            />

            <Section title="1. Payment for Physical Merchandise" testid="payments-physical">
                <P
                    supportEmail={supportEmail}
                    text="Payments collected through the website are accepted against orders for physical fishing products and related merchandise. For every successfully completed order, the customer is entitled to the physical product or products recorded against that order, subject to these policies."
                />
            </Section>

            <Section title="2. Payment Processing" testid="payments-processing">
                <P
                    supportEmail={supportEmail}
                    text="Online payments may be processed through Razorpay or another payment service provider displayed at checkout. Available payment methods may include UPI, cards, net banking or other methods enabled by the payment service provider. Opensea Fishing Tackles does not store complete card, UPI PIN, net-banking password or similar sensitive payment credentials. A payment is treated as successful only after confirmation is received from the payment service provider and the order is recorded as paid in our system."
                />
            </Section>

            <Section title="3. Mystery / Undisclosed Product Purchases" testid="payments-mystery">
                <P
                    supportEmail={supportEmail}
                    text="For a mystery/undisclosed-product order, the customer pays the displayed order amount for physical fishing merchandise, while the exact product is allocated after successful payment. Every successfully paid order receives a physical product. The allocated product is recorded against the order after payment. An invoice is generated for the actual physical product supplied. Where applicable, a promotional discount may be reflected on the invoice so that the invoice total corresponds to the payable order amount. The payment does not create a cash balance or cash-withdrawal entitlement and no cash alternative is offered in place of the physical product unless a refund is due under our policies."
                />
            </Section>

            <Section title="4. Multiple and Repeat Purchases" testid="payments-multiple">
                <P
                    supportEmail={supportEmail}
                    text="Customers may place multiple orders or make repeat purchases using the same verified contact details, subject to stock availability, order limits, fraud-prevention checks and any specific conditions shown for an offer."
                />
            </Section>

            <Section title="5. Failed, Pending or Duplicate Payments" testid="payments-failed">
                <P
                    supportEmail={supportEmail}
                    text="If a payment fails, the order is not considered paid unless our system subsequently receives successful confirmation. If a customer's account is debited but the order is not confirmed, the customer should contact us with the payment reference so the transaction can be verified. Any duplicate payment confirmed by us will be refunded to the original payment method after verification. Bank/payment-provider processing times may apply in addition to the refund-processing time stated by us."
                />
            </Section>

            <Section title="6. Cancellations and Refunds" testid="payments-cancel-refund">
                <P
                    supportEmail={supportEmail}
                    text="Eligible orders cancelled before dispatch receive a full refund to the original payment method. Opensea Fishing Tackles normally processes an approved pre-dispatch refund within 2 to 4 working days. The customer's bank or payment provider may require additional time to reflect the amount after we process the refund."
                />
            </Section>

            <Section title="7. Payment Disputes and Contact" testid="payments-disputes">
                <P
                    supportEmail={supportEmail}
                    text="Payment-provider terms may also apply to the processing of the transaction."
                />
                <ContactLine
                    supportEmail={supportEmail}
                    phone={PHONE}
                    label="For any payment issue, please contact us first at"
                />
            </Section>
        </div>
    );
}

// ---------------------------------------------------------------------------
// 3. SHIPPING POLICY  — verbatim from approved document section "3."
// ---------------------------------------------------------------------------
function ShippingBody({ supportEmail }) {
    return (
        <div className="space-y-8" data-testid="policy-shipping-body">
            <P
                supportEmail={supportEmail}
                text="This Shipping Policy applies to all physical-product orders placed with Opensea Fishing Tackles, including regular webstore products, sale/clearance products, bulk or multiple-quantity orders, and mystery/undisclosed-product orders."
            />

            <Section title="1. Service Area" testid="shipping-service-area">
                <P
                    supportEmail={supportEmail}
                    text="We ship across India through available courier partners, subject to destination pincode serviceability and any product-specific transport restrictions."
                />
            </Section>

            <Section title="2. Dispatch and Delivery" testid="shipping-dispatch">
                <P
                    supportEmail={supportEmail}
                    text="Standard delivery is generally 3 to 7 business days from dispatch, depending on destination, courier serviceability and operating conditions. Order processing and dispatch time may vary based on stock location, product size, order quantity, packing requirements and holidays. Tracking details are shared by SMS, email, WhatsApp or another available channel once the shipment is dispatched, subject to courier tracking availability."
                />
            </Section>

            <Section title="3. Shipping Charges" testid="shipping-charges">
                <P
                    supportEmail={supportEmail}
                    text="Shipping charges, if any, are shown at checkout or otherwise communicated before dispatch. Free-shipping thresholds may be offered and may change from time to time. Heavy, long, oversized or special-handling items such as fishing rods, large reels or bulk consignments may carry additional freight or handling charges."
                />
            </Section>

            <Section title="4. Mystery / Undisclosed Product Orders" testid="shipping-mystery">
                <P
                    supportEmail={supportEmail}
                    text="Mystery/undisclosed-product orders are physical-product orders and follow the same shipping process as other merchandise. After successful payment, the product is allocated, recorded against the order and invoiced. The physical product is then packed and dispatched to the customer's delivery address."
                />
            </Section>

            <Section title="5. Delivery Address and Serviceability" testid="shipping-address">
                <P
                    supportEmail={supportEmail}
                    text="Customers are responsible for providing a complete and accurate delivery address, pincode and contact number. If a pincode is not serviceable, we may contact the customer for an alternate deliverable address or cancel and refund the order. Additional charges caused by an incorrect address, repeated failed delivery attempts or customer-requested re-routing may be payable by the customer where permitted."
                />
            </Section>

            <Section title="6. Courier Delays and Delivery Attempts" testid="shipping-delays">
                <P
                    supportEmail={supportEmail}
                    text="Delivery dates are estimates and may be affected by courier operations, weather, local restrictions, remote-location serviceability, holidays or other events outside our reasonable control. We will assist with tracking and escalation where reasonably possible."
                />
            </Section>

            <Section title="7. Damaged Parcels" testid="shipping-damaged">
                <P
                    supportEmail={supportEmail}
                    text="If a parcel appears damaged or the product is damaged in transit, please preserve the packaging and follow the evidence and reporting requirements in our Return & Cancellation Policy. A clear unboxing video from the sealed-package stage is strongly required for transit-damage verification."
                />
            </Section>

            <Section title="8. Contact" testid="shipping-contact">
                <ContactLine
                    supportEmail={supportEmail}
                    phone={PHONE}
                    label="For shipping or tracking support, contact"
                />
            </Section>
        </div>
    );
}

// ---------------------------------------------------------------------------
// 4. RETURN & CANCELLATION POLICY  — verbatim from approved document section "4."
// ---------------------------------------------------------------------------
function ReturnsBody({ supportEmail }) {
    return (
        <div className="space-y-8" data-testid="policy-returns-body">
            <P
                supportEmail={supportEmail}
                text="Opensea Fishing Tackles sells specialised fishing tackle and related physical merchandise. Products are checked before dispatch and packed for shipment. This policy explains cancellation, refund, transit-damage and return handling."
            />

            <Section title="1. Cancellation Before Dispatch" testid="returns-before-dispatch">
                <P
                    supportEmail={supportEmail}
                    text="An order may be cancelled for a full refund only before it has been dispatched. To request cancellation, contact us immediately with the order number using the contact details below. Once an approved cancellation is confirmed, we normally process the full refund to the original payment method within 2 to 4 working days. The customer's bank or payment provider may require additional time after we process the refund for the amount to appear in the customer's account."
                />
                <ContactLine supportEmail={supportEmail} phone={PHONE} label="" />
            </Section>

            <Section title="2. Cancellation After Dispatch" testid="returns-after-dispatch">
                <P
                    supportEmail={supportEmail}
                    text="Once an order has been dispatched, it cannot normally be cancelled. Any issue after dispatch or delivery will be handled under the applicable return, transit-damage, wrong-item or other claim provisions below."
                />
            </Section>

            <Section title="3. General Returns and Exchanges" testid="returns-general">
                <P
                    supportEmail={supportEmail}
                    text="Because fishing tackle includes specialised, packaged and usage-sensitive products, we do not offer general returns or exchanges after delivery merely because the customer changes their mind, selects the wrong specification, or no longer wants the product. This does not affect rights that cannot lawfully be excluded."
                />
            </Section>

            <Section title="4. Mystery / Undisclosed Product Orders" testid="returns-mystery">
                <P
                    supportEmail={supportEmail}
                    text="For mystery/undisclosed-product orders, the customer agrees before purchase that the exact eligible product is not disclosed before payment. Every successfully paid order receives a physical product. Such an order cannot be returned or exchanged merely because the customer would have preferred a different product. However, legitimate claims involving transit damage, an item different from the product recorded on the invoice/order, or another legally valid issue may still be considered under this policy."
                />
            </Section>

            <Section title="5. Transit Damage Claims" testid="returns-transit">
                <P
                    supportEmail={supportEmail}
                    text="A replacement or refund may be considered where a product is physically damaged during shipping. Record a clear, continuous unboxing video beginning from the sealed package before opening it. Report visible transit damage within 24 hours of delivery wherever possible. In exceptional cases, a claim may be considered up to a maximum of 48 hours from delivery at our discretion and subject to available evidence. Share the unboxing video, clear photographs, order number and a description of the issue with our support team. Do not discard the product, packaging, labels or shipping material until the claim has been resolved."
                />
            </Section>

            <Section title="6. Wrong Item or Order Discrepancy" testid="returns-wrong-item">
                <P
                    supportEmail={supportEmail}
                    text="If the product delivered is different from the product recorded on the invoice/order, contact us within 48 hours of delivery with the order number, unboxing video and photographs. After verification, we may arrange correction, replacement or refund as appropriate."
                />
            </Section>

            <Section title="7. Non-Returnable Items" testid="returns-non-returnable">
                <List
                    testid="returns-non-returnable-list"
                    items={[
                        "Used fishing rods, reels, lines, lures, hooks, terminal tackle and accessories.",
                        "Products that have been installed, spooled, rigged, modified, customised or used.",
                        "Hooks, terminal tackle, opened fishing line and other products where use or opening makes resale unsuitable, except where a valid defect or wrong-item claim applies.",
                        "Products damaged because of misuse, incorrect handling, improper storage or use contrary to manufacturer guidance.",
                        "Claims without reasonable supporting evidence where such evidence is necessary to verify the issue.",
                    ]}
                />
            </Section>

            <Section title="8. Approved Replacement or Refund" testid="returns-approved">
                <P
                    supportEmail={supportEmail}
                    text="If a valid claim is approved after verification, we may offer a replacement, correction, store-supported resolution or refund depending on the nature of the issue, product availability and applicable law. Approved refunds are made to the original payment method unless another method is required by law or mutually agreed."
                />
            </Section>

            <Section title="9. Return Authorisation" testid="returns-auth">
                <P
                    supportEmail={supportEmail}
                    text="Do not send a product back without first contacting our support team and receiving return instructions. Unauthorised return shipments may be refused or may delay resolution."
                />
            </Section>

            <Section title="10. Contact" testid="returns-contact">
                <ContactLine
                    supportEmail={supportEmail}
                    phone={PHONE}
                    label="For cancellation, return, transit-damage or order-discrepancy assistance, contact"
                />
            </Section>
        </div>
    );
}

// ---------------------------------------------------------------------------
// 5. PRIVACY POLICY  — unchanged substantive content. (No promotional-draw
// callout; only the obsolete `PromotionalDrawFallbackNotice` was removed.)
// ---------------------------------------------------------------------------
function PrivacyBody({ supportEmail }) {
    return (
        <div className="space-y-6" data-testid="policy-privacy-body">
            <Section title="What we collect and why" testid="privacy-collect">
                <p>
                    We collect only the personal information necessary to fulfil your order and provide
                    customer service — typically your name, delivery address, phone number and email.
                </p>
                <p>
                    Your data is never sold. Payment card, UPI or net-banking credentials are handled by
                    Razorpay under their own secure infrastructure and are not stored by Opensea Fishing
                    Tackles.
                </p>
                <p>
                    You can request deletion of your data at any time by contacting our support team at{" "}
                    <a
                        href={`mailto:${supportEmail}`}
                        className="text-[#00BCE5] hover:underline"
                        data-testid="privacy-support-email"
                    >
                        {supportEmail}
                    </a>
                    .
                </p>
            </Section>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Slug registry. Legacy `promotional-draws` and `promotional-draws/shipping`
// routes no longer serve their old content — they redirect to the matching
// current policy page (see the top-level Policy component below). The `tnc`
// slug remains a working alias of `terms` because the external Monster Catch
// Worker footer still links there.
// ---------------------------------------------------------------------------
function getPolicy(fullSlug, supportEmail) {
    switch (fullSlug) {
        case "terms":
        case "tnc":
            return {
                title: "Terms & Conditions",
                description: `Terms & Conditions — ${STORE_NAME}. Business and products, orders, payments, shipping, returns, website use, limitation and contact.`,
                node: <TermsBody supportEmail={supportEmail} />,
            };
        case "payments":
            return {
                title: "Payment Terms",
                description: `Payment Terms — ${STORE_NAME}. Payment processing, mystery/undisclosed-product purchases, multiple purchases, failed/duplicate payments, cancellations and disputes.`,
                node: <PaymentsBody supportEmail={supportEmail} />,
            };
        case "shipping":
            return {
                title: "Shipping Policy",
                description: `Shipping Policy — ${STORE_NAME}. India serviceability, dispatch, charges, mystery/undisclosed-product orders, address, courier delays and damaged parcels.`,
                node: <ShippingBody supportEmail={supportEmail} />,
            };
        case "returns":
            return {
                title: "Return & Cancellation Policy",
                description: `Return & Cancellation Policy — ${STORE_NAME}. Cancellation before/after dispatch, general returns, mystery/undisclosed orders, transit damage, wrong item, non-returnables, refunds.`,
                node: <ReturnsBody supportEmail={supportEmail} />,
            };
        case "privacy":
            return {
                title: "Privacy Policy",
                description: `Privacy Policy — ${STORE_NAME}. What we collect and why, and how to contact us about your data.`,
                node: <PrivacyBody supportEmail={supportEmail} />,
            };
        default:
            return null;
    }
}

const LEGACY_REDIRECTS = {
    "promotional-draws": "/policy/terms",
    "promotional-draws/shipping": "/policy/shipping",
};

export default function Policy() {
    const { slug, sub } = useParams();
    const { settings } = useSettings();
    const supportEmail = settings?.contact_email || "osefishingtackles@gmail.com";
    const fullSlug = sub ? `${slug}/${sub}` : slug;
    const data = getPolicy(fullSlug, supportEmail);
    const legacyRedirect = LEGACY_REDIRECTS[fullSlug];
    const navigate = useNavigate();

    // Redirect legacy promotional-draw URLs to their current-policy
    // counterparts. replace:true so the back button doesn't trap users.
    useEffect(() => {
        if (legacyRedirect) {
            navigate(legacyRedirect, { replace: true });
        }
    }, [legacyRedirect, navigate]);

    const title = data?.title || "Policy";
    const description = data
        ? data.description
        : `${STORE_NAME} — policies for orders, shipping and customer service.`;

    if (legacyRedirect) {
        // Render nothing while the redirect resolves.
        return null;
    }

    // Canonical: the `tnc` alias shares the primary `/policy/terms` canonical
    // so Google doesn't see two URLs for the same Terms document.
    const canonicalPath =
        fullSlug === "tnc" ? "/policy/terms" : `/policy/${fullSlug}`;

    return (
        <div
            className="container mx-auto px-4 py-10 max-w-3xl"
            data-testid={`policy-${fullSlug ? fullSlug.replace(/\//g, "-") : "unknown"}`}
        >
            <SEO
                title={`${title} | ${STORE_NAME}`}
                description={description}
                path={canonicalPath}
            />
            <h1 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tight">
                {title}
            </h1>
            {data && fullSlug !== "privacy" && (
                <p
                    className="mt-2 text-xs uppercase tracking-widest text-white/40"
                    data-testid="policy-effective-date"
                >
                    Effective: {EFFECTIVE_DATE}
                </p>
            )}
            <div className="mt-6">
                {data ? (
                    data.node
                ) : (
                    <p className="text-white/75" data-testid="policy-unknown">
                        This policy page is not available. Please visit our Terms, Shipping, Returns or
                        Privacy pages from the footer.
                    </p>
                )}
            </div>
        </div>
    );
}
